/**
 * How an onboarding applicant's answers read back, shared by both consoles.
 *
 * The client console shows a submitted section as these rows (its
 * `ReviewSectionBlock`, in Review and submit and on every frozen section),
 * and the issuer console's Onboarding tab shows the same rows, so a reviewer
 * reads each answer under the label the applicant saw. Pure: labels, value
 * spellings and payload-to-rows only.
 *
 * Moved verbatim from yaas-client `pages/Onboarding/reviewSummary.ts` and
 * `stepReadiness.ts`, which now import it.
 */

/** Mirrors yaas-api `OnboardingStepName`. */
export type ApplicantOnboardingStepName =
    | "use_case_and_intent"
    | "legal_entity"
    | "tax_identification"
    | "signatory_authority"
    | "beneficial_ownership"
    | "sanctions_screening"
    | "source_of_funds"
    | "wallet_declarations"
    | "msa_signing"
    | "minting_agreement_signing";

/** Each section's title as the applicant's rail names it. */
export const APPLICANT_STEP_DISPLAY_LABEL: Record<ApplicantOnboardingStepName, string> = {
    use_case_and_intent: "Use Case & Intent",
    legal_entity: "Legal Entity",
    tax_identification: "Tax Identification",
    signatory_authority: "Authorized Signatory",
    beneficial_ownership: "Beneficial Ownership",
    sanctions_screening: "Sanctions Screening",
    source_of_funds: "Funds & Operations",
    wallet_declarations: "Wallet Declarations",
    msa_signing: "Master Services Agreement",
    minting_agreement_signing: "Minting Master Agreement"
};

export interface ApplicantReviewRow {
    label: string;
    value: string;
}

/**
 * How a payload key is named to the applicant. Anything unmapped falls back
 * to a humanised key, which reads correctly for the plainly-named fields
 * and makes a newly added one legible without a code change here.
 */
const FIELD_LABEL: Record<string, string> = {
    useCases: "Use cases",
    expectedCapitalRange: "Expected capital",
    yieldDistribution: "Yield distribution",
    intentDescription: "Intent",
    legalName: "Legal name",
    tradingName: "Trading name",
    entityType: "Entity type",
    registeredAddress: "Registered address",
    companyNumber: "Company number",
    incorporationJurisdiction: "Country of incorporation",
    primaryContactName: "Primary contact",
    primaryContactEmail: "Primary contact email",
    legalContactName: "Legal contact",
    legalContactEmail: "Legal contact email",
    taxIdValue: "Tax ID",
    taxIdJurisdiction: "Tax jurisdiction",
    fatcaClassification: "FATCA classification",
    signatoryName: "Signatory",
    signatoryEmail: "Signatory email",
    signatoryTitle: "Signatory title",
    sourceCategory: "Source of funds",
    sourceDescription: "Description",
    ownershipPercent: "Ownership",
    role: "Role",
    entries: "Beneficial owner"
};

/** Fields the applicant entered as a percentage, shown back with the sign. */
const PERCENT_FIELDS: ReadonlySet<string> = new Set(["ownershipPercent"]);

/** Enum values the applicant picked, spelled the way the form spelled them. */
const VALUE_LABEL: Record<string, string> = {
    treasury: "Treasury",
    liquidity_provider: "Liquidity provider",
    protocol_owned_liquidity: "Protocol-owned liquidity",
    neobank: "Neobank",
    other: "Other",
    under_500k: "Under $500k",
    "500k_to_2m": "$500k to $2M",
    "2m_to_10m": "$2M to $10M",
    over_10m: "Over $10M",
    pass_through: "Passed through to users",
    protocol_retains: "Protocol retains",
    protocol_distributes: "Protocol distributes"
};

/**
 * An uploaded file is referenced by uuid in the payload. The uuid tells the
 * applicant nothing, so the row says whether the document is on file.
 */
export const APPLICANT_DOCUMENT_FIELD_LABEL: Record<string, string> = {
    formationDocumentUuid: "Formation document",
    operatingDocumentUuid: "Operating document",
    certificateOfGoodStandingUuid: "Certificate of good standing",
    taxIdDocumentUuid: "Tax ID document",
    authorizationDocumentUuid: "Authorization document",
    supportingDocumentUuid: "Supporting document",
    governmentIdUuid: "Government ID"
};

export function reviewFieldLabel(key: string): string {
    return (
        FIELD_LABEL[key] ??
        APPLICANT_DOCUMENT_FIELD_LABEL[key] ??
        key
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .replace(/[_-]+/g, " ")
            .replace(/^./, (first) => first.toUpperCase())
    );
}

function formatScalar(value: unknown, key?: string): string {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "number") return key && PERCENT_FIELDS.has(key) ? `${value}%` : String(value);
    if (typeof value === "string") return VALUE_LABEL[value] ?? value;
    return "";
}

export function isEmptyReviewValue(value: unknown): boolean {
    if (value === null || value === undefined || value === "") return true;
    if (Array.isArray(value)) return value.length === 0 || value.every(isEmptyReviewValue);
    if (typeof value === "object") return Object.values(value as Record<string, unknown>).every(isEmptyReviewValue);
    return false;
}

function appendRows(rows: ApplicantReviewRow[], label: string, value: unknown, key?: string): void {
    if (isEmptyReviewValue(value)) return;

    if (Array.isArray(value)) {
        const entries = value.filter((item) => !isEmptyReviewValue(item));
        if (entries.every((item) => typeof item !== "object" || item === null)) {
            rows.push({
                label,
                value: entries
                    .map((item) => formatScalar(item, key))
                    .filter(Boolean)
                    .join(", ")
            });
            return;
        }
        entries.forEach((entry, index) => appendRows(rows, `${label} ${index + 1}`, entry));
        return;
    }

    if (typeof value === "object") {
        for (const [nestedKey, nested] of Object.entries(value as Record<string, unknown>)) {
            // A per-entry uuid identifies the row to the API; it is
            // bookkeeping, not something the applicant told us.
            if (nestedKey === "entryUuid" || nestedKey === "entryStatus") continue;
            appendRows(rows, `${label} · ${reviewFieldLabel(nestedKey)}`, nested, nestedKey);
        }
        return;
    }

    rows.push({ label, value: formatScalar(value, key) });
}

/** The rows for one step's payload, in payload order. */
export function reviewRowsForPayload(payload: Record<string, unknown> | null): ApplicantReviewRow[] {
    if (!payload) return [];
    const rows: ApplicantReviewRow[] = [];
    for (const [key, value] of Object.entries(payload)) {
        if (isEmptyReviewValue(value)) continue;
        if (key in APPLICANT_DOCUMENT_FIELD_LABEL) {
            rows.push({ label: APPLICANT_DOCUMENT_FIELD_LABEL[key], value: "Uploaded" });
            continue;
        }
        appendRows(rows, reviewFieldLabel(key), value, key);
    }
    return rows;
}
